import type { PaymentActorId, PaymentKind } from '@/types';

export type PaymentActorVocab = 'plain' | 'ops' | 'iso';

export interface PaymentActorLabel {
  label: string;
  tag?: string;
}

type LabelTable = Record<PaymentActorId, PaymentActorLabel>;

const PLAIN_KEYS: Record<PaymentActorId, string> = {
  payer: 'explorer.actor.payer',
  bankA: 'explorer.actor.bankA',
  csm: 'explorer.actor.csm',
  bankB: 'explorer.actor.bankB',
  beneficiary: 'explorer.actor.beneficiary',
  scheme: 'explorer.actor.scheme',
  merchant: 'explorer.actor.merchant',
  acquirer: 'explorer.actor.acquirer',
};

/** Resolve party / bank labels for the payment explorer vocabulary toggle. */
export function paymentActorLabel(
  actor: PaymentActorId,
  kind: PaymentKind,
  vocab: PaymentActorVocab,
  t: (key: string) => string,
): PaymentActorLabel {
  if (vocab === 'plain') {
    return { label: t(PLAIN_KEYS[actor]) };
  }

  const table = vocab === 'ops' ? opsLabels(kind, t) : isoLabels(t);
  return table[actor];
}

function opsLabels(kind: PaymentKind, t: (key: string) => string): LabelTable {
  const isSdd = kind === 'direct-debit';
  return {
    payer: isSdd
      ? { label: t('explorer.actor.payer') }
      : { label: t('home.overview.roleOriginator'), tag: 'Orig' },
    bankA: { label: t('explorer.actor.bankA'), tag: 'DbtrAgt' },
    csm: { label: t('explorer.actor.csm') },
    bankB: { label: t('explorer.actor.bankB'), tag: 'CdtrAgt' },
    beneficiary: isSdd
      ? { label: t('home.overview.roleOriginator'), tag: 'Orig' }
      : { label: t('home.overview.roleBeneficiary'), tag: 'Bene' },
    scheme: { label: t('explorer.actor.scheme') },
    merchant: { label: t('explorer.actor.merchant') },
    acquirer: { label: t('explorer.actor.acquirer') },
  };
}

function isoLabels(t: (key: string) => string): LabelTable {
  return {
    payer: { label: t('home.overview.roleDebtor'), tag: 'Dbtr' },
    bankA: { label: t('home.overview.bankDebtor'), tag: 'DbtrAgt' },
    csm: { label: t('explorer.actor.csm') },
    bankB: { label: t('home.overview.bankCreditor'), tag: 'CdtrAgt' },
    beneficiary: { label: t('home.overview.roleCreditor'), tag: 'Cdtr' },
    scheme: { label: t('explorer.actor.scheme') },
    merchant: { label: t('explorer.actor.merchant') },
    acquirer: { label: t('explorer.actor.acquirer') },
  };
}

export function formatPaymentActorLabel({ label, tag }: PaymentActorLabel): string {
  return tag ? `${label} (${tag})` : label;
}

export function paymentActorText(
  actor: PaymentActorId,
  kind: PaymentKind,
  vocab: PaymentActorVocab,
  t: (key: string) => string,
): string {
  return formatPaymentActorLabel(paymentActorLabel(actor, kind, vocab, t));
}

export function parsePaymentActorVocab(raw: string | null): PaymentActorVocab {
  if (raw === 'ops' || raw === 'iso') return raw;
  return 'plain';
}
